// Phase 16.1 — Lifted Network MVP
//
// The internal social/community surface of the Lifted Ecosystem. Today this
// is the destination behind the "Lifted Network" sidebar item (previously
// labeled "Launchpad"). Direct messages, groups, audio/video meetings and
// in-app MP4 uploads are P1 — they appear here as clearly-labeled stubs
// rather than fake placeholders.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, MessageCircle, Send, Pin, Image as ImageIcon, Video, Megaphone,
  Search, Circle, Lock, Sparkles, Users, Trash2,
} from "lucide-react";
import MediaUploader from "@/components/lifted/MediaUploader";

import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLE_BADGE = {
  master_admin: { label: "Master Admin", className: "bg-lifted-green/15 text-lifted-green border-lifted-green/40" },
  admin:        { label: "Admin",        className: "bg-lifted-green/15 text-lifted-green border-lifted-green/40" },
  affiliate:    { label: "Affiliate",    className: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
  driver:       { label: "Driver",       className: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
  partner:      { label: "Partner",      className: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  franchisee:   { label: "Franchise",    className: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  customer:     { label: "Customer",     className: "bg-white/10 text-white/70 border-white/20" },
};

function RoleChip({ role }) {
  const cfg = ROLE_BADGE[role] || ROLE_BADGE.customer;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[0.65rem] font-bebas tracking-[0.18em] uppercase ${cfg.className}`}
      data-testid={`role-chip-${role}`}
    >
      {cfg.label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function Composer({ onCreated, isMaster }) {
  const [body, setBody]   = useState("");
  const [media, setMedia] = useState([]); // [{id, url, content_type}]
  const [pinned, setPinned] = useState(false);
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showMedia, setShowMedia] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setBusy(true); setErr("");
    try {
      const firstImage = media.find((m) => !m.content_type?.startsWith("video/"));
      const firstVideo = media.find((m) => m.content_type?.startsWith("video/"));
      const { data } = await api.post("/network/posts", {
        body: body.trim(),
        image_url: firstImage?.url || null,
        video_url: firstVideo?.url || null,
        pinned: !!pinned,
        tag: tag || null,
      });
      setBody(""); setMedia([]); setPinned(false); setTag(""); setShowMedia(false);
      onCreated?.(data.post);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="glass-panel p-5" data-testid="network-composer">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-lifted-green" />
        <div className="font-bebas tracking-[0.22em] text-xs text-lifted-dim">SHARE WITH THE ECOSYSTEM</div>
      </div>
      <textarea
        className="w-full bg-black/40 rounded-lg border border-white/10 px-3 py-3 text-sm text-lifted-text focus:border-lifted-green/60 focus:outline-none resize-none"
        rows={3}
        placeholder={isMaster ? "Broadcast an update… (Master Admin can pin)" : "What's lifting your day?"}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        data-testid="network-composer-body"
      />
      {showMedia && (
        <div className="mt-3">
          <MediaUploader
            value={media}
            onChange={setMedia}
            purpose="network_post"
            maxFiles={4}
            allowVideo={true}
            primaryEnabled={false}
            testId="network-composer-uploader"
          />
        </div>
      )}
      {err && <div className="mt-3 text-xs text-rose-300">{err}</div>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setShowMedia((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-lifted-dim hover:text-lifted-text border border-white/10 rounded-md px-2.5 py-1.5"
          data-testid="network-composer-toggle-media"
        >
          <ImageIcon className="h-3 w-3" /> <Video className="h-3 w-3" /> Upload media
        </button>
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs"
          data-testid="network-composer-tag"
        >
          <option value="">No tag</option>
          <option value="affiliate">Affiliate</option>
          <option value="driver">Driver</option>
          <option value="partner">Partner</option>
          <option value="franchise">Franchise</option>
          <option value="admin">Admin</option>
          <option value="training">Training</option>
          <option value="system">System</option>
        </select>
        {isMaster && (
          <label className="inline-flex items-center gap-1.5 text-xs text-lifted-dim cursor-pointer">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
              data-testid="network-composer-pin"
            />
            <Pin className="h-3 w-3" /> Pin
          </label>
        )}
        <div className="ml-auto">
          <button
            type="submit"
            disabled={busy || !body.trim()}
            className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="network-composer-submit"
          >
            <Send className="h-3.5 w-3.5" />
            {busy ? "Sharing…" : (pinned ? "Pin Broadcast" : "Share")}
          </button>
        </div>
      </div>
    </form>
  );
}

function PostCard({ post, onChanged }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const role = post.author?.primary_role || (post.author?.roles || [])[0] || "customer";
  const roles = user?.roles || [];
  const isMine  = user && post.author?.id === user.id;
  const isAdmin = roles.includes("admin") || roles.includes("master_admin");
  const canDelete = isMine || isAdmin;

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/network/posts/${post.id}/like`);
      setLiked(data.liked); setLikeCount(data.like_count);
      onChanged?.();
    } catch (_) { /* noop */ }
  };

  const openComments = async () => {
    setShowComments((v) => !v);
    if (!commentsLoaded) {
      try {
        const { data } = await api.get(`/network/posts/${post.id}/comments`);
        setComments(data.comments || []);
        setCommentsLoaded(true);
      } catch (_) { /* noop */ }
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    const t = commentText.trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/network/posts/${post.id}/comments`, { body: t });
      setComments((c) => [...c, data.comment]);
      setCommentText("");
    } catch (_) { /* noop */ }  // eslint-disable-line no-unused-vars
  };

  const deletePost = async () => {
    if (!confirm("Delete this post? This cannot be undone.")) return;  // eslint-disable-line no-restricted-globals, no-alert
    setDeleting(true);
    try {
      await api.delete(`/network/posts/${post.id}`);
      setDeleted(true);
      onChanged?.();
    } catch (_) { /* noop */ }  // eslint-disable-line no-unused-vars
    finally { setDeleting(false); }
  };

  if (deleted) return null;

  return (
    <article
      className={`glass-panel p-5 ${post.pinned ? "border border-lifted-green/30 shadow-[0_0_30px_-12px_rgba(184,255,103,0.45)]" : ""}`}
      data-testid={`network-post-${post.id}`}
    >
      {post.pinned && (
        <div className="flex items-center gap-1.5 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-green mb-2">
          <Pin className="h-3 w-3" /> PINNED BROADCAST
        </div>
      )}
      <header className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-lifted-green/15 text-lifted-green font-bebas tracking-[0.18em] flex items-center justify-center">
          {(post.author?.name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-medium text-lifted-text">{post.author?.name || "Lifted Member"}</div>
            <RoleChip role={role} />
            {post.tag && (
              <span className="text-[0.6rem] tracking-[0.18em] uppercase font-bebas text-lifted-dim border border-white/10 rounded-full px-2 py-0.5">
                {post.tag}
              </span>
            )}
            <span className="text-[0.65rem] text-lifted-dim">{timeAgo(post.created_at)}</span>
          </div>
        </div>
        {canDelete && (
          <button onClick={deletePost} disabled={deleting}
                  className="text-lifted-dim hover:text-rose-300 transition disabled:opacity-50"
                  title={isMine ? "Delete my post" : "Moderate (admin)"}
                  data-testid={`network-post-delete-${post.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </header>

      <div className="mt-3 text-sm whitespace-pre-wrap text-lifted-text/95 leading-relaxed">
        {post.body}
      </div>
      {post.image_url && (
        <div className="mt-3 overflow-hidden rounded-lg border border-white/10">
          <img src={post.image_url} alt="" className="w-full max-h-[500px] object-cover" loading="lazy" />
        </div>
      )}
      {post.video_url && (
        <div className="mt-3 overflow-hidden rounded-lg border border-white/10 bg-black">
          <video src={post.video_url} controls playsInline className="w-full max-h-[520px]" />
        </div>
      )}

      <footer className="mt-4 flex items-center gap-4 text-xs text-lifted-dim">
        <button
          onClick={toggleLike}
          className={`inline-flex items-center gap-1.5 ${liked ? "text-rose-300" : "hover:text-lifted-text"}`}
          data-testid={`network-post-like-${post.id}`}
        >
          <Heart className={`h-4 w-4 ${liked ? "fill-rose-400 text-rose-400" : ""}`} />
          <span>{likeCount}</span>
        </button>
        <button
          onClick={openComments}
          className="inline-flex items-center gap-1.5 hover:text-lifted-text"
          data-testid={`network-post-comments-${post.id}`}
        >
          <MessageCircle className="h-4 w-4" /> <span>{post.comment_count || comments.length || 0}</span>
        </button>
      </footer>

      {showComments && (
        <div className="mt-4 space-y-3 border-t border-white/5 pt-4">
          {comments.length === 0 && commentsLoaded && (
            <div className="text-xs text-lifted-dim">Be the first to reply.</div>
          )}
          {comments.map((c, idx) => (
            <div key={`${c.id || "c"}-${idx}`} className="text-sm">
              <div className="flex items-center gap-2 text-[0.7rem] text-lifted-dim">
                <span className="text-lifted-text font-medium">{c.author?.name}</span>
                <RoleChip role={c.author?.primary_role || "customer"} />
                <span>{timeAgo(c.created_at)}</span>
              </div>
              <div className="mt-0.5">{c.body}</div>
            </div>
          ))}
          <form onSubmit={addComment} className="flex items-center gap-2">
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a reply…"
              className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/50 focus:outline-none"
              data-testid={`network-post-comment-input-${post.id}`}
            />
            <button type="submit" className="btn-ghost !py-2 !px-3 text-xs" data-testid={`network-post-comment-submit-${post.id}`}>
              <Send className="h-3.5 w-3.5" />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

function OnlineDirectory({ online, onMessage }) {
  return (
    <aside className="glass-panel p-4" data-testid="network-online">
      <div className="flex items-center justify-between mb-3">
        <div className="font-bebas tracking-[0.22em] text-xs text-lifted-text inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-lifted-green" /> ONLINE NOW
        </div>
        <span className="text-[0.65rem] text-lifted-dim">{online.length}</span>
      </div>
      {online.length === 0 ? (
        <div className="text-xs text-lifted-dim leading-relaxed">
          No one online in the last 5 minutes. As members open the Network their presence will light up here.
        </div>
      ) : (
        <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {online.map((u, idx) => (
            <li
              key={`${u.user_id || "u"}-${idx}`}
              className="flex items-center gap-3 rounded-md hover:bg-white/5 px-2 py-1.5 cursor-pointer"
              onClick={() => onMessage?.(u)}
              data-testid={`network-online-${u.user_id}`}
            >
              <div className="relative">
                <div className="h-8 w-8 rounded-full bg-lifted-green/15 text-lifted-green text-[0.65rem] font-bebas tracking-[0.18em] flex items-center justify-center">
                  {(u.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <Circle className="h-2.5 w-2.5 absolute -bottom-0.5 -right-0.5 text-lifted-green fill-lifted-green" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-lifted-text truncate">{u.name || u.email}</div>
                <div className="text-[0.6rem] text-lifted-dim">{(u.primary_role || "customer").replace("_", " ")}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function MemberSearch({ onMessage }) {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (!q && !role) { setResults([]); setSearched(false); return; }
      try {
        const { data } = await api.get(`/network/users/search`, { params: { q, role: role || undefined } });
        if (!cancelled) { setResults(data.results || []); setSearched(true); }
      } catch (_) { /* noop */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, role]);

  return (
    <div className="glass-panel p-4" data-testid="network-search">
      <div className="font-bebas tracking-[0.22em] text-xs text-lifted-text mb-3 inline-flex items-center gap-1.5">
        <Search className="h-3.5 w-3.5 text-lifted-green" /> FIND A MEMBER
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Name, email…"
        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/50 focus:outline-none"
        data-testid="network-search-input"
      />
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="mt-2 w-full bg-black/40 border border-white/10 rounded-md px-2 py-2 text-xs"
        data-testid="network-search-role"
      >
        <option value="">All roles</option>
        <option value="master_admin">Master Admin</option>
        <option value="admin">Admin</option>
        <option value="affiliate">Affiliate</option>
        <option value="driver">Driver</option>
        <option value="partner">Partner</option>
        <option value="franchisee">Franchise</option>
        <option value="customer">Customer</option>
      </select>
      {searched && (
        <ul className="mt-3 space-y-2 max-h-[300px] overflow-y-auto pr-1">
          {results.length === 0 && (
            <li className="text-xs text-lifted-dim">No members match.</li>
          )}
          {results.map((u) => (
            <li
              key={u.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 cursor-pointer"
              onClick={() => onMessage?.(u)}
              data-testid={`network-search-result-${u.id}`}
            >
              <div className="relative">
                <div className="h-7 w-7 rounded-full bg-white/10 text-lifted-text text-[0.65rem] font-bebas flex items-center justify-center">
                  {(u.name || "?").slice(0, 1).toUpperCase()}
                </div>
                {u.online && <Circle className="h-2 w-2 absolute -bottom-0.5 -right-0.5 text-lifted-green fill-lifted-green" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-lifted-text truncate">{u.name || u.email}</div>
                <div className="text-[0.6rem] text-lifted-dim">{(u.primary_role || "customer").replace("_", " ")}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ComingSoonPanel() {
  return (
    <div className="glass-panel p-4" data-testid="network-coming-soon">
      <div className="font-bebas tracking-[0.22em] text-xs text-lifted-text mb-2 inline-flex items-center gap-1.5">
        <Lock className="h-3.5 w-3.5 text-lifted-dim" /> COMING SOON
      </div>
      <ul className="text-xs text-lifted-dim space-y-1.5 leading-relaxed">
        <li>• Direct messages + group threads</li>
        <li>• Role channels (Affiliate / Driver / Partner / Franchise)</li>
        <li>• Audio &amp; video meetings + webinars</li>
        <li>• In-app MP4 / file uploads (needs storage key)</li>
        <li>• Push notifications</li>
      </ul>
    </div>
  );
}

export default function Network() {
  const { user } = useAuth();
  const [posts, setPosts]     = useState([]);
  const [online, setOnline]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");

  const isMaster = useMemo(
    () => !!(user && user.roles && user.roles.includes("master_admin")),
    [user],
  );

  const loadFeed = useCallback(async () => {
    try {
      const { data } = await api.get("/network/feed");
      setPosts(data.posts || []);
    } catch (e) {
      if (e?.response?.status !== 401) setErr(apiErrorMessage(e));
    }
  }, []);

  const loadOnline = useCallback(async () => {
    try {
      const { data } = await api.get("/network/online");
      setOnline(data.online || []);
    } catch (_) { /* noop */ }
  }, []);

  // Initial load + heartbeat loop
  useEffect(() => {
    if (!user || user === false) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { await api.post("/network/heartbeat"); } catch (_) {}
      await loadFeed();
      await loadOnline();
      if (!cancelled) setLoading(false);
    })();
    const hb = setInterval(() => {
      api.post("/network/heartbeat").catch(() => {});
      loadOnline();
    }, 60_000);
    return () => { cancelled = true; clearInterval(hb); };
  }, [user, loadFeed, loadOnline]);

  const onCreated = (post) => {
    setPosts((p) => [post, ...p.filter((x) => x.id !== post.id)]);
  };

  const sendFirstBroadcast = async () => {
    try {
      const { data } = await api.post("/network/posts", {
        body:
          "🚀 Welcome to the Lifted Network — our internal home base.\n\n" +
          "This is where the ecosystem talks. Every affiliate, driver, partner, " +
          "and franchisee can post wins, share updates, ask questions, and " +
          "celebrate milestones. Together we build. Together we rise. Together " +
          "we're LIFTED.",
        pinned: true,
        tag: "admin",
      });
      setPosts((p) => [data.post, ...p]);
    } catch (e) {
      setErr(apiErrorMessage(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="network-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-2 border-lifted-green/30 border-t-lifted-green animate-spin" />
          <div className="font-bebas tracking-[0.3em] text-lifted-dim text-xs">LOADING LIFTED NETWORK…</div>
        </div>
      </div>
    );
  }

  const showFirstBroadcast = isMaster && posts.length === 0;

  return (
    <div className="space-y-6" data-testid="network-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-lifted-green font-bebas tracking-[0.22em] text-[0.7rem]">LIFTED NETWORK</div>
          <h1 className="font-anton text-3xl sm:text-4xl uppercase text-white leading-[0.95] mt-1" data-testid="network-page-title">
            The Lifted Ecosystem Launchpad
          </h1>
          <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
            Share wins, broadcast updates, drop questions — this is the home base for affiliates,
            drivers, partners, franchisees, and admins.
          </p>
        </div>
        {isMaster && (
          <button
            onClick={sendFirstBroadcast}
            className="btn-lifted !py-2.5 !px-4 text-xs inline-flex items-center gap-2"
            data-testid="network-first-broadcast"
          >
            <Megaphone className="h-4 w-4" />
            Send First Ecosystem Broadcast
          </button>
        )}
      </header>

      {err && (
        <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm" data-testid="network-error">
          {err}
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        {/* Feed column */}
        <div className="space-y-5">
          <Composer onCreated={onCreated} isMaster={isMaster} />

          {showFirstBroadcast && (
            <div className="glass-panel p-8 text-center border border-lifted-green/20">
              <Megaphone className="h-8 w-8 text-lifted-green mx-auto mb-3" />
              <div className="font-bebas tracking-[0.22em] text-sm text-white">YOUR NETWORK IS QUIET</div>
              <p className="text-lifted-dim text-sm mt-2 max-w-md mx-auto">
                Kick things off with your first broadcast — every affiliate, driver, partner and
                franchisee will see it the moment they open the Lifted Network.
              </p>
              <button
                onClick={sendFirstBroadcast}
                className="btn-lifted mt-4 !py-2.5 !px-5 text-xs inline-flex items-center gap-2"
                data-testid="network-first-broadcast-empty"
              >
                <Megaphone className="h-4 w-4" /> Send My First Broadcast
              </button>
            </div>
          )}

          {posts.length === 0 && !showFirstBroadcast && (
            <div className="glass-panel p-6 text-center text-sm text-lifted-dim">
              No posts yet. Be the first to share something.
            </div>
          )}

          {posts.map((p, idx) => (
            <PostCard key={`${p.id || "post"}-${idx}`} post={p} onChanged={loadFeed} />
          ))}
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          <OnlineDirectory online={online} />
          <MemberSearch />
          <ComingSoonPanel />
          <Link
            to="/dashboard"
            className="glass-panel p-3 inline-flex w-full items-center justify-center text-xs text-lifted-dim hover:text-lifted-text"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
